/**
 * LibraryEntryDialog — one connector of the list, opened in a centred window
 * (item 64, 2026-10-29): the same shell as "Add connector" (ConnectorDialog's
 * overlay, header and footer), with its Settings, the Agents that use it and
 * its History as tabs. Saving is in the Settings tab's footer
 * (LibrarySettings), so nothing about one connector opens a window over
 * another window. It replaced the drawer that slid in from the side
 * (operator decision 2026-10-21, option A).
 *
 * An entry shared from an organization above says so in one banner, with
 * "Restore their version" when this organization changed it; the fields set
 * above are read-only lines. An entry this organization hid has no Settings
 * to change until it is restored.
 *
 * A modal window like every connector window: focus moves in, Tab stays in,
 * and focus returns to the row on close (useDialogFocus). Every way out — the
 * close button, Escape, a click beside the window, the form's Cancel — asks
 * first while an edit is not saved. Escape belongs to the window in front: a
 * question or a conflict opened over this one answers it, and an open select
 * list closes first.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfigProvider, Dropdown, type MenuProps } from 'antd';
import { useDialogFocus, useFrontWindowEscape } from '@hooks/useDialogFocus';
import { useLanguage } from '@hooks/useLanguage';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { mirrorPlacement } from '@/utils/rtl';
import {
  connectorLibraryService,
  type ChangeOutcome,
  type LibraryCard,
  type LibraryEntryEditable,
} from '../services/connectorLibraryService';
import { restoreTarget, rowStatus } from '../utils/libraryList';
import { KindTag, MoreGlyph, ServiceIcon } from './LibraryBits';
import { LibrarySettings } from './LibrarySettings';
import { AgentsPanel, HistoryPanel } from './LibraryPanels';
import { OrganizationsPanel } from './LibraryOrganizations';
import { LibrarySignIn } from './LibrarySignIn';
import type { SignInValue } from '@modules/chatbots/utils/signIn';
import '@modules/chatbots/components/BotModal.css';
import '@modules/chatbots/components/ConnectorDialog.css';

export type EntryTab = 'settings' | 'signin' | 'organizations' | 'agents' | 'history';

interface Props {
  tenantId: string;
  organizationName: string;
  descendantCount: number;
  /** The row it was opened from: shown at once, while the entry loads. */
  row: LibraryCard;
  tab: EntryTab;
  onTab: (tab: EntryTab) => void;
  /** The row's actions (refresh, hide / delete, restore) — the page asks first and does them. */
  menu: MenuProps['items'];
  onRestore: () => void;
  busy: boolean;
  onClose: () => void;
  onSaved: (entry: LibraryEntryEditable, outcome: ChangeOutcome | null) => void;
  /** Who gets it changed (Organizations tab): the list reloads its counts. */
  onReachChanged: () => void;
  /** Opened right after adding: the Sign-in tab starts on the choice made there. */
  startSignIn?: SignInValue;
}

export function LibraryEntryDialog({
  tenantId, organizationName, descendantCount, row, tab, onTab, menu, onRestore, busy, onClose, onSaved, onReachChanged,
  startSignIn,
}: Props) {
  const { t, direction } = useLanguage();
  const [detail, setDetail] = useState<LibraryEntryEditable | null>(null);
  const [error, setError] = useState(false);
  // The Settings form holds an edit not saved yet: closing asks first.
  const [dirty, setDirty] = useState(false);
  // The Organizations tab holds a choice not saved yet.
  const [orgsDirty, setOrgsDirty] = useState(false);
  // Opened once, the Organizations tab stays mounted (like Settings), so a
  // choice in progress survives a look at another tab.
  const [orgsOpened, setOrgsOpened] = useState(tab === 'organizations');
  useEffect(() => { setOrgsOpened(false); setOrgsDirty(false); }, [row.entry_id]);
  useEffect(() => { if (tab === 'organizations') setOrgsOpened(true); }, [tab, row.entry_id]);
  const [askDiscard, setAskDiscard] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Only the latest read lands: a save and a status change can each start one.
  const latest = useRef(0);
  // Focus moves in, Tab stays in, and focus goes back to the row on close.
  useDialogFocus(dialogRef, overlayRef);

  const load = useCallback(async () => {
    const mine = ++latest.current;
    setError(false);
    try {
      const entry = await connectorLibraryService.get(row.entry_id, tenantId);
      if (mine === latest.current) setDetail(entry);
    } catch {
      if (mine === latest.current) setError(true);
    }
  }, [row.entry_id, tenantId]);

  // Another entry: start from nothing. The same entry changed under it (a
  // save, a restore, a refresh): read it again, keeping what is on screen.
  useEffect(() => { setDetail(null); setDirty(false); }, [row.entry_id]);

  const requestClose = useCallback(() => {
    if (dirty || orgsDirty) setAskDiscard(true); else onClose();
  }, [dirty, orgsDirty, onClose]);
  useEffect(() => { void load(); }, [load, row.edit_version, row.removed_here, row.status]);

  useEffect(() => { closeRef.current?.focus(); }, [row.entry_id]);

  // Escape closes the window in FRONT only — the discard question, a
  // conflict or a confirmation over this window answers it itself, and an
  // open select list closes first — and asks first while an edit is unsaved.
  useFrontWindowEscape(overlayRef, requestClose);

  const status = rowStatus(row);
  const hidden = row.removed_here;
  const restoreTo = restoreTarget(row);
  // A hidden entry has no settings to change until it is restored.
  const shown: EntryTab = hidden && (tab === 'settings' || tab === 'signin') ? 'agents' : tab;
  const tabs: { key: EntryTab; label: string; disabled?: boolean }[] = [
    { key: 'settings', label: t('connectorLibrary.drawer.settings'), disabled: hidden },
    { key: 'signin', label: t('connectorLibrary.drawer.signin'), disabled: hidden },
    { key: 'organizations', label: t('connectorLibrary.drawer.organizations') },
    { key: 'agents', label: row.usage.total > 0
      ? t('connectorLibrary.drawer.agentsCount', { count: row.usage.total }) : t('connectorLibrary.drawer.agents') },
    { key: 'history', label: t('connectorLibrary.drawer.history') },
  ];

  return createPortal(
    // A click beside the window closes it — asking first while an edit is
    // not saved, so a stray click never throws one away.
    <div className="modal-overlay cxd-overlay" role="presentation" ref={overlayRef}
         onMouseDown={e => { if (e.target === e.currentTarget) requestClose(); }}>
      <div className="modal cxd cxd--wide cxl-entry" role="dialog" aria-modal="true" aria-label={row.name}
           data-testid="library-entry" ref={dialogRef} tabIndex={-1}>
        {/* Menus, selects and tooltips open inside the window, so they stay
            above it and inside its focus trap. */}
        <ConfigProvider getPopupContainer={() => dialogRef.current ?? document.body}>
        <div className="modal-header cxl-entry__head">
          <ServiceIcon name={row.name} slug={row.slug} url={row.kind === 'mcp' ? row.url : row.base_url} />
          <div className="modal-header-content cxl-entry__titles">
            <h2 className="modal-title cxl-entry__title"><bdi dir="auto">{row.name}</bdi></h2>
            <span className="cxl-entry__pills">
              <KindTag kind={row.kind} />
              {row.inherited ? (
                <span className="cx-pill" data-testid="library-entry-origin">
                  <span>{t('connectorLibrary.origin.from')} <span dir="auto">{row.owner_name}</span></span>
                </span>
              ) : (
                <span className="cx-pill cx-pill--accent" data-testid="library-entry-origin">{t('connectorLibrary.origin.yours')}</span>
              )}
              {status && <StatusPill status={status} />}
            </span>
          </div>
          <div className="cxl-entry__head-actions">
            <Dropdown menu={{ items: menu }} overlayClassName="cxl-menu" trigger={['click']}
                      placement={mirrorPlacement(direction, 'bottomRight')}>
              <button type="button" className="cxl-icon-btn" aria-label={t('connectorLibrary.menu.label', { name: row.name })}
                      disabled={busy} data-testid="library-entry-menu">
                <MoreGlyph />
              </button>
            </Dropdown>
            <button type="button" className="modal-close-btn" ref={closeRef} onClick={requestClose}
                    aria-label={t('common.close')} data-testid="library-entry-close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {row.inherited && (
          <div className="cxl-entry__banner" role="note" data-testid="library-entry-banner">
            <span>
              {hidden
                ? t('connectorLibrary.drawer.hiddenBanner', { owner: row.owner_name ?? '', organization: organizationName, count: descendantCount })
                : t('connectorLibrary.drawer.sharedBanner', { owner: row.owner_name ?? '', organization: organizationName, count: descendantCount })}
            </span>
            {(hidden || row.edited_here) && (
              <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={onRestore}
                      data-testid="library-entry-restore">
                {hidden ? t('connectorLibrary.menu.unhide') : t('connectorLibrary.menu.restore', { owner: restoreTo })}
              </button>
            )}
          </div>
        )}
        {!row.inherited && (
          <p className="cxl-entry__scope">
            {descendantCount > 0
              ? t('connectorLibrary.drawer.ownScopeTree', { organization: organizationName, count: descendantCount })
              : t('connectorLibrary.drawer.ownScope', { organization: organizationName })}
          </p>
        )}

        <div className="cxl-entry__tabs" role="tablist">
          {tabs.map(x => (
            <button key={x.key} type="button" role="tab" aria-selected={shown === x.key} disabled={x.disabled}
                    className={`cxl-entry__tab ${shown === x.key ? 'is-on' : ''}`} onClick={() => onTab(x.key)}
                    data-testid={`library-entry-tab-${x.key}`}>
              {x.label}
            </button>
          ))}
        </div>

        <div className={`cxl-entry__panel cxl-entry__panel--${shown}`} role="tabpanel">
          {/* Settings stay mounted while another tab is open, so an edit in
              progress survives a look at the agents or the history. */}
          {!hidden && (
            <div className="cxl-entry__pane" hidden={shown !== 'settings'}>
            {error ? (
              <div className="cxl-panel__error" role="alert">
                <span>{t('connectorLibrary.errors.loadEntry')}</span>{' '}
                <button type="button" className="cx-btn cx-btn--sm" onClick={() => void load()}>{t('common.retry')}</button>
              </div>
            ) : !detail ? (
              <div className="cxl-loading" aria-busy="true"><span className="loading-spinner" /></div>
            ) : (
              <LibrarySettings key={`${detail.entry_id}-${detail.edit_version}`} tenantId={tenantId} detail={detail}
                               onReload={load} onClose={requestClose} onDirty={setDirty}
                               onSaved={(entry, outcome) => { setDirty(false); onSaved(entry, outcome); }}
                               onOpenSignIn={() => onTab('signin')} />
            )}
            </div>
          )}
          {shown === 'signin' && !hidden && (
            detail
              ? <LibrarySignIn tenantId={tenantId} detail={detail} startSignIn={startSignIn}
                               onChanged={() => { void load(); onReachChanged(); }} />
              : <div className="cxl-loading" aria-busy="true"><span className="loading-spinner" /></div>
          )}
          {orgsOpened && (
            <div className="cxl-entry__pane" hidden={shown !== 'organizations'}>
              <OrganizationsPanel entryId={row.entry_id} tenantId={tenantId} onDirty={setOrgsDirty}
                                  onChanged={() => onReachChanged()} />
            </div>
          )}
          {shown === 'agents' && <AgentsPanel entryId={row.entry_id} tenantId={tenantId} />}
          {shown === 'history' && <HistoryPanel entryId={row.entry_id} tenantId={tenantId} />}
        </div>
        </ConfigProvider>
      </div>
      <ConfirmModal
        isOpen={askDiscard}
        onClose={() => setAskDiscard(false)}
        onConfirm={() => { setAskDiscard(false); setDirty(false); onClose(); }}
        title={t('connectorLibrary.discard.title', { name: row.name })}
        message={t('connectorLibrary.discard.body')}
        confirmText={t('connectorLibrary.discard.confirm')}
        cancelText={t('connectorLibrary.discard.keep')}
        variant="warning"
      />
    </div>,
    document.body,
  );
}

export function StatusPill({ status }: { status: NonNullable<ReturnType<typeof rowStatus>> }) {
  const { t } = useLanguage();
  if (status === 'signin') {
    return <span className="cx-pill cxl-pill--edited" data-status="signin">{t('connectorLibrary.status.signInNeeded')}</span>;
  }
  if (status === 'failed') {
    return <span className="cx-pill cx-pill--error" data-status="failed">{t('connectorLibrary.status.failed')}</span>;
  }
  if (status === 'hidden') {
    return <span className="cx-pill cxl-pill--muted" data-status="hidden">{t('connectorLibrary.origin.removedHere')}</span>;
  }
  return <span className="cx-pill cxl-pill--edited" data-status="changed">{t('connectorLibrary.origin.editedHere')}</span>;
}
